import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGENTS = [
  {
    name: 'Jeff Sklar',
    key: 'jeff',
    email: 'j.sklar@spiregrp.com',
    phone: '403.483.5561',
    province: 'Alberta'
  },
  {
    name: 'Michael Permack',
    key: 'michael',
    email: 'm.permack@spiregrp.com',
    phone: '403.462.5560',
    calendly: 'https://calendly.com/michaelpermack/',
    province: 'British Columbia'
  },
  {
    name: 'Jason Shantz',
    key: 'jason',
    email: 'j.shantz@spiregrp.com',
    phone: '403.874.1129',
    province: 'Saskatchewan'
  }
];

// Steps: greeting → needs → agent → contact → done
const STEP = {
  GREETING: 'greeting',
  NEEDS: 'needs',
  AGENT: 'agent',
  CONTACT: 'contact',
  DONE: 'done'
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(STEP.GREETING);
  const [needsText, setNeedsText] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize chat on open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          sender: 'bot',
          message: "Hi! Welcome to Spire Group. 👋",
        },
        {
          sender: 'bot',
          message: "We specialize in healthcare real estate across North America — from dental clinics and pharmacies to practice spaces.",
        },
        {
          sender: 'bot',
          message: "What can we help you with today?",
          showNeedsOptions: true
        }
      ]);
      setStep(STEP.NEEDS);
    }
  }, [isOpen]);

  const addBot = (message, extra = {}) => {
    setMessages(prev => [...prev, { sender: 'bot', message, ...extra }]);
  };

  const addUser = (message) => {
    setMessages(prev => [...prev, { sender: 'user', message }]);
  };

  const handleNeedsOption = (option) => {
    setNeedsText(option);
    addUser(option);
    setTimeout(() => {
      addBot("Great! Who would you like to connect with?", { showAgentOptions: true });
      setStep(STEP.AGENT);
    }, 300);
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    addUser(`I'd like to connect with ${agent.name}`);
    setTimeout(() => {
      addBot(
        `${agent.name} would love to help you! Please share your name, email, and phone number and they'll reach out within 24 hours.`
      );
      setStep(STEP.CONTACT);
    }, 300);
  };

  const extractContactInfo = (message) => {
    const emailMatch = message.match(/[\w.-]+@[\w.-]+\.\w+/);
    const phoneMatch = message.match(/(\+?1?\s?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/);
    const parts = message.split(/[\n,]+/);
    let name = parts[0]?.replace(/^(name:|my name is|i'm|i am)\s*/i, '').trim();
    if (emailMatch && name === emailMatch[0]) name = null;
    return {
      name: name || null,
      email: emailMatch ? emailMatch[0] : null,
      phone: phoneMatch ? phoneMatch[0] : null
    };
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const userMessage = inputMessage.trim();
    setInputMessage('');
    addUser(userMessage);
    setIsLoading(true);

    try {
      if (step === STEP.CONTACT && selectedAgent) {
        const info = extractContactInfo(userMessage);
        if (info.email || info.phone || userMessage.length > 5) {
          // Submit lead
          try {
            await axios.post(`${API}/leads/create`, {
              session_id: sessionId,
              name: info.name || 'Not provided',
              email: info.email || '',
              phone: info.phone || '',
              province: selectedAgent.province,
              interest_type: 'Healthcare Real Estate',
              needs_description: needsText,
              selected_agent: selectedAgent.name,
              assigned_to: selectedAgent.email
            });
          } catch (e) {
            console.error('Lead error:', e);
          }

          const msg = selectedAgent.calendly
            ? `Thank you! I've notified ${selectedAgent.name} and they'll be in touch soon.\n\nYou can also book a meeting directly: ${selectedAgent.calendly}`
            : `Thank you! I've notified ${selectedAgent.name} (${selectedAgent.phone}). They'll reach out within 24 hours.`;

          addBot(msg);
          addBot("Is there anything else I can help you with?", { showRestartOption: true });
          setStep(STEP.DONE);
          setIsLoading(false);
          return;
        }
      }

      // Fallback: general AI chat
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMessage
      });
      addBot(response.data.bot_response);
    } catch (error) {
      addBot("Please contact us directly at info@spiregrp.com");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestart = () => {
    setMessages([]);
    setStep(STEP.GREETING);
    setNeedsText('');
    setSelectedAgent(null);
    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const needsOptions = [
    "Find a new practice space",
    "Lease renewal or renegotiation",
    "Site selection & demographics",
    "General inquiry"
  ];

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-spire-purple to-spire-purple-light text-white rounded-full shadow-2xl hover:scale-110 z-50 flex items-center justify-center transition-transform"
        >
          <MessageCircle size={28} />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-spire-purple to-spire-purple-light text-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src="/spire-logo.png" alt="Spire" className="h-7 brightness-0 invert" />
              <div>
                <p className="text-xs text-white/80">Healthcare Real Estate Consultants</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-1.5 transition">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-spire-purple text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                  }`}>
                    {msg.message.split('\n').map((line, li) => (
                      <span key={li}>
                        {line.startsWith('http') ? (
                          <a href={line} target="_blank" rel="noopener noreferrer" className="underline text-spire-green break-all">{line}</a>
                        ) : line}
                        {li < msg.message.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Needs quick-replies */}
                {msg.showNeedsOptions && step === STEP.NEEDS && (
                  <div className="flex flex-col gap-2 mt-3 ml-1">
                    {needsOptions.map(opt => (
                      <button
                        key={opt}
                        onClick={() => handleNeedsOption(opt)}
                        className="text-left px-4 py-2.5 bg-white border border-spire-purple/30 text-spire-purple rounded-xl text-sm font-medium hover:bg-spire-purple hover:text-white transition-all shadow-sm"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {/* Agent selection buttons */}
                {msg.showAgentOptions && step === STEP.AGENT && (
                  <div className="flex flex-col gap-2 mt-3 ml-1">
                    {AGENTS.map(agent => (
                      <button
                        key={agent.key}
                        onClick={() => handleAgentSelect(agent)}
                        className="text-left px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm shadow-sm hover:border-spire-purple hover:shadow-md transition-all"
                      >
                        <span className="font-semibold text-gray-900">{agent.name}</span>
                        <span className="text-gray-500 text-xs block">{agent.phone}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Restart option */}
                {msg.showRestartOption && step === STEP.DONE && (
                  <div className="mt-3 ml-1">
                    <button
                      onClick={handleRestart}
                      className="px-4 py-2 bg-spire-purple/10 text-spire-purple rounded-xl text-sm font-medium hover:bg-spire-purple hover:text-white transition-all"
                    >
                      Start a new conversation
                    </button>
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
                  <Loader className="animate-spin text-spire-purple" size={16} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input — show after needs are captured or in done state */}
          {(step === STEP.CONTACT || step === STEP.DONE) && (
            <div className="p-3 bg-white border-t flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={step === STEP.CONTACT ? "Name, email, phone number..." : "Type a message..."}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-spire-purple/30 text-sm"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !inputMessage.trim()}
                  className="w-10 h-10 bg-spire-purple text-white rounded-full flex items-center justify-center disabled:opacity-40 hover:bg-spire-purple-light transition flex-shrink-0"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
